import React, { useCallback, useEffect, useState, useRef } from "react";
import axiosInstance from "src/api/axios";
import { RefreshCw } from "lucide-react";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ChatUser {
  user_id: string;
  username: string | null;
  lastMessageTime: string;
}

const Chats: React.FC<{ igUsername: string }> = ({ igUsername }) => {
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [igId, setIgId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch IG ID from /instagram-business/user
  useEffect(() => {
    const fetchIgId = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/user`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined,
        );
        const userData =
          response.data && response.data.data && response.data.data[0];
        if (userData && userData.user_id) {
          setIgId(userData.user_id);
        }
      } catch (err: any) {
        setError("Failed to fetch IG ID");
      }
    };
    fetchIgId();
  }, []);

  // Fetch chat users (conversations)
  const fetchUsers = useCallback(async () => {
    if (!igId) return;
    setLoadingUsers(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${backendUrl}/instagram-business/chats?ig_id=${igId}`,
      );
      setUsers(response.data.users || []);
    } catch (err: any) {
      setError("Failed to fetch chat users");
    } finally {
      setLoadingUsers(false);
    }
  }, [igId]);

  // Fetch chat history for the selected user
  const fetchMessages = useCallback(async () => {
    if (!igId || !selectedUser) return;
    setLoadingMessages(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${backendUrl}/instagram-business/chat-history?ig_id=${igId}&user_id=${selectedUser}`,
      );
      setMessages(response.data.messages || []);
    } catch (err: any) {
      setError("Failed to fetch chat history");
    } finally {
      setLoadingMessages(false);
    }
  }, [igId, selectedUser]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Refresh button: re-fetch the latest conversations and the open thread.
  // const handleRefresh = useCallback(async () => {
  //   if (!igId) return;
  //   setRefreshing(true);
  //   try {
  //     await Promise.all([fetchUsers(), fetchMessages()]);
  //   } finally {
  //     setRefreshing(false);
  //   }
  // }, [igId, fetchUsers, fetchMessages]);
  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Refresh messages from Instagram API
  const handleRefresh = async () => {
    if (!igId || !selectedUser) return;
    setRefreshing(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${backendUrl}/instagram-business/refresh-chat?ig_id=${igId}&user_id=${selectedUser}`,
      );
      setMessages(response.data.messages || []);
    } catch (err: any) {
      setError("Failed to refresh messages");
    } finally {
      setRefreshing(false);
    }
  };

  // Send message to selected user
  const handleSendMessage = async () => {
    if (!igId || !selectedUser || !messageInput.trim()) return;
    setSending(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${backendUrl}/instagram-business/send-message`,
        {
          ig_id: igId,
          recipient_id: selectedUser,
          message: messageInput.trim(),
        },
      );
      if (response.data.success) {
        setMessageInput("");
        // Refresh messages to show the sent message
        handleRefresh();
      }
    } catch (err: any) {
      setError("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex h-96 border rounded-lg overflow-hidden bg-white">
      {/* Sidebar: Users */}
      <div className="w-1/3 border-r bg-gray-50 overflow-y-auto">
        <div className="font-bold p-2 border-b flex items-center justify-between">
          <span>Chats</span>
          <button
            onClick={handleRefresh}
            disabled={refreshing || !igId}
            title="Refresh conversations"
            aria-label="Refresh conversations"
            className="text-gray-500 hover:text-blue-600 disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
        {loadingUsers ? (
          <div className="p-2 text-gray-500">Loading users...</div>
        ) : error ? (
          <div className="p-2 text-red-500">{error}</div>
        ) : users.length === 0 ? (
          <div className="p-2 text-gray-500">No chats found.</div>
        ) : (
          <ul>
            {users
              .filter((user) => user.username !== igUsername)
              .map((user) => (
                <li
                  key={user.user_id}
                  className={`p-3 cursor-pointer hover:bg-blue-100 border-b ${
                    selectedUser === user.user_id
                      ? "bg-blue-100 font-semibold"
                      : ""
                  }`}
                  onClick={() => setSelectedUser(user.user_id)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {user.username || user.user_id}
                    </span>
                    {user.username && (
                      <span className="text-xs text-gray-500">
                        ID: {user.user_id}
                      </span>
                    )}
                    {user.lastMessageTime && (
                      <span className="text-xs text-gray-400 mt-1">
                        {new Date(user.lastMessageTime).toLocaleString()}
                      </span>
                    )}
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>
      {/* Main: Messages */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="font-bold p-2 border-b flex items-center justify-between">
          {selectedUser ? (
            <div className="flex items-center gap-2">
              <span>Messages with </span>
              <span className="text-blue-600">
                {users.find((u) => u.user_id === selectedUser)?.username ||
                  selectedUser}
              </span>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="ml-2 p-1.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                title="Refresh messages"
              >
                <svg
                  className={`w-4 h-4 text-gray-600 ${
                    refreshing ? "animate-spin" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>
          ) : (
            "Messages"
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {loadingMessages ? (
            <div className="text-gray-500">Loading messages...</div>
          ) : error ? (
            <div className="text-red-500">{error}</div>
          ) : messages.length === 0 ? (
            <div className="text-gray-500">No messages found.</div>
          ) : (
            messages
              .filter((msg) => msg.text && msg.text.trim() !== "")
              .map((msg, idx) => {
                // Check if message is from selected user by comparing sender_id
                const isFromSelectedUser = msg.sender_id === selectedUser;
                const senderName = isFromSelectedUser
                  ? msg.sender_username ||
                    users.find((u) => u.user_id === selectedUser)?.username ||
                    "User"
                  : "You";

                return (
                  <div
                    key={msg._id || idx}
                    className={`flex flex-col max-w-[70%] ${
                      isFromSelectedUser
                        ? "mr-auto items-start"
                        : "ml-auto items-end"
                    }`}
                  >
                    <span
                      className={`text-xs font-medium mb-1 ${
                        isFromSelectedUser ? "text-purple-600" : "text-blue-600"
                      }`}
                    >
                      {senderName}
                    </span>
                    <div
                      className={`rounded-lg px-3 py-2 ${
                        isFromSelectedUser
                          ? "bg-purple-100 text-left"
                          : "bg-blue-100 text-right"
                      } max-w-[300px]`}
                    >
                      <div className="text-sm break-words whitespace-pre-wrap">
                        {msg.text}
                      </div>
                      <div
                        className={`text-xs mt-1 ${
                          isFromSelectedUser
                            ? "text-purple-400"
                            : "text-blue-400"
                        }`}
                      >
                        {msg.timestamp
                          ? new Date(msg.timestamp).toLocaleString()
                          : ""}
                      </div>
                    </div>
                  </div>
                );
              })
          )}
          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
        {/* Message Input */}
        {selectedUser && (
          <div className="p-2 border-t bg-gray-50">
            <div className="flex gap-2">
              <input
                type="text"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                disabled={sending}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <button
                onClick={handleSendMessage}
                disabled={sending || !messageInput.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sending ? (
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                )}
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chats;
